import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from '../utils/axios';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';

// Create the context
const ChatContext = createContext();

// Hook for using the context
export const useChat = () => useContext(ChatContext);

// Global tracking variable to prevent duplicate chats
if (typeof window !== 'undefined') {
  window.lastCreatedChatId = null;
}

// Provider component
export const ChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typing, setTyping] = useState(false);
  const websocket = useRef(null);
  const { token } = useAuth();
  const { selectedClient } = useClient();

  // Load user's chats
  const loadChats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/v1/chat');
      
      // Ensure we're getting unique chats by ID
      const uniqueChats = Array.from(new Map(response.data.map(chat => [chat.id, chat])).values());
      console.log(`Loaded ${uniqueChats.length} unique chats out of ${response.data.length} total`);
      
      // Sort chats by updated_at timestamp (newest first)
      const sortedChats = [...uniqueChats].sort((a, b) => {
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      
      setChats(sortedChats);
      return sortedChats;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load chats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load specific chat with messages
  const loadChat = useCallback(async (chatId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/v1/chat/${chatId}`);
      setCurrentChat(response.data);
      setMessages(response.data.messages);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load chat');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new chat
  const createChat = async (title) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post('/api/v1/chat', { title });
      
      // Add the new chat to the list, avoiding duplicates
      setChats(prev => {
        // Remove any existing chat with the same ID
        const filtered = prev.filter(chat => chat.id !== response.data.id);
        // Add the new chat to the beginning of the list
        return [response.data, ...filtered];
      });
      
      // Set as current chat
      setCurrentChat(response.data);
      setMessages([]);
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create chat');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Send a message via REST API
  const sendMessage = async (message, chatId = null, clientData = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use current chat ID if not specified
      const targetChatId = chatId || currentChat?.id;
      
      // If client data wasn't provided but we have a selected client, use that
      if (!clientData && selectedClient) {
        clientData = {
          client_id: selectedClient.id,
          client_name: selectedClient.company_name,
          client_contact: selectedClient.contact_name,
          client_email: selectedClient.email
        };
      }
      
      const response = await axios.post('/api/v1/chat/message', {
        message,
        chat_id: targetChatId,
        client_data: clientData
      });
      
      // If no chat is loaded or a new chat was created
      if (!currentChat || !targetChatId) {
        // Reload chats to get the new chat
        await loadChats();
        
        // Find and load the new chat
        const chat = chats.find(c => c.id === response.chat_id);
        if (chat) {
          await loadChat(chat.id);
        }
      } else {
        // Add the new messages to the current chat
        const userMessage = {
          id: Date.now(), // Temporary ID
          content: message,
          role: 'user',
          created_at: new Date().toISOString()
        };
        
        const assistantMessage = {
          id: Date.now() + 1, // Temporary ID
          content: response.content,
          role: 'assistant',
          created_at: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, userMessage, assistantMessage]);
      }
      
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Initialize WebSocket connection
  const initWebSocket = useCallback(() => {
    if (!token) return;
    
    // Close any existing connection
    if (websocket.current) {
      // Closing existing WebSocket connection
      websocket.current.close();
    }
    
    try {
      // Create new WebSocket connection
      const baseUrlWithoutHttp = axios.defaults.baseURL.replace(/^https?:\/\//, '');
      // Connecting WebSocket
      
      // Create WebSocket connection with specific event handlers
      // Use wss:// for HTTPS connections, ws:// for HTTP
      const wsProtocol = axios.defaults.baseURL.startsWith('https') ? 'wss://' : 'ws://';
      const wsUrl = `${wsProtocol}${baseUrlWithoutHttp}/api/v1/chat/ws/${token}`;
      // Connecting to WebSocket URL
      websocket.current = new WebSocket(wsUrl);
      
      // Add error logging
      websocket.current.onerror = (event) => {
        console.error('WebSocket connection error:', event);
        // Don't set error state immediately, let reconnection handle it
      };
      
      websocket.current.onclose = (event) => {
        console.log('[WEBSOCKET] Disconnected');
        
        // Clear intervals if they exist
        if (websocket.current && websocket.current.pingInterval) {
          clearInterval(websocket.current.pingInterval);
        }
        
        if (websocket.current && websocket.current.healthCheckInterval) {
          clearInterval(websocket.current.healthCheckInterval);
        }
        
        // Try to reconnect after a delay if not intentionally closed
        if (event.code !== 1000 && event.code !== 1001) {
          console.log('[WEBSOCKET] Reconnecting...');
          setTimeout(() => {
            // Reconnecting WebSocket
            if (token) {
              initWebSocket();
            }
          }, 3000); // Increased delay to 3 seconds
        }
      };
      
      // Add a timeout to check if WebSocket connection was established
      setTimeout(() => {
        if (websocket.current && websocket.current.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection not established within timeout. Reconnecting...');
          initWebSocket();
        }
      }, 5000);
      
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
    
    // Connection opened
    websocket.current.onopen = () => {
      console.log('[WEBSOCKET] Connected');
      
      // Send an immediate check_state message to verify connection
      setTimeout(() => {
        if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
          try {
            websocket.current.send(JSON.stringify({ 
              event: 'check_state',
              timestamp: new Date().toISOString()
            }));
            // Sent connection state check
          } catch (err) {
            console.error('Error sending state check:', err);
          }
        }
      }, 1000);
      
      // Set up ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
          try {
            websocket.current.send(JSON.stringify({ 
              event: 'ping',
              timestamp: new Date().toISOString() 
            }));
          } catch (err) {
            console.error('Error sending ping:', err);
          }
        } else {
          clearInterval(pingInterval);
          // Try to reconnect
          if (token) {
            console.log('Attempting to reconnect WebSocket');
            initWebSocket();
          }
        }
      }, 45000); // Send ping every 45 seconds to reduce connection churn
      
      // Store interval for cleanup
      websocket.current.pingInterval = pingInterval;
      
      // Set up a check interval to verify connection health
      const healthCheckInterval = setInterval(() => {
        if (websocket.current) {
          if (websocket.current.readyState === WebSocket.CLOSED || 
              websocket.current.readyState === WebSocket.CLOSING) {
            console.log('WebSocket connection unhealthy, attempting to reconnect');
            clearInterval(healthCheckInterval);
            if (token) {
              initWebSocket();
            }
          } else if (websocket.current.readyState === WebSocket.OPEN) {
            // Send a health check message
            try {
              websocket.current.send(JSON.stringify({ 
                event: 'check_state',
                timestamp: new Date().toISOString()
              }));
              // Sent health check
            } catch (err) {
              console.error('Error sending health check:', err);
            }
          }
        } else {
          clearInterval(healthCheckInterval);
        }
      }, 60000); // Check connection health every 60 seconds
      
      // Store interval for cleanup
      websocket.current.healthCheckInterval = healthCheckInterval;
    };
    
    // Listen for messages
    websocket.current.onmessage = (event) => {
      try {
        // WebSocket message received
        const data = JSON.parse(event.data);
        
        if (data.event === 'typing') {
          console.log('Typing status received:', data.status);
          setTyping(data.status);
        } else if (data.event === 'message_sent') {
          // User message sent confirmation
          console.log('Message sent confirmation received:', data.message.id);
          setMessages(prev => {
            // Check if the message already exists to prevent duplicates
            const exists = prev.some(msg => msg.id === data.message.id);
            if (exists) return prev;
            
            const newMessage = {
              id: data.message.id,
              content: data.message.content,
              role: data.message.role,
              created_at: data.message.created_at,
              metadata: data.message.metadata || {}
            };
            
            // Emit a custom event with the agent information
            if (newMessage.metadata && Object.keys(newMessage.metadata).length > 0) {
              console.log('Emitting agent-info event with metadata:', newMessage.metadata);
              window.dispatchEvent(new CustomEvent('agent-info', {
                detail: {
                  metadata: newMessage.metadata,
                  messageId: newMessage.id
                }
              }));
            }
            
            return [...prev, newMessage];
          });
        } else if (data.event === 'message_received') {
          // Assistant message received
          console.log('Assistant response received:', data.message);
          setLoading(false); // Important: set loading to false when response arrives
          
          // Extra validation to ensure we have valid data
          if (!data.message || !data.message.id || !data.message.content) {
            console.error('Received invalid message_received event:', data);
            return;
          }
          
          setMessages(prev => {
            // Debug: Log existing messages to help troubleshoot
            console.log('Current messages:', prev.map(m => ({ id: m.id, role: m.role })));
            
            // Check if the message already exists to prevent duplicates
            const exists = prev.some(msg => msg.id === data.message.id);
            if (exists) {
              console.log(`Message with id ${data.message.id} already exists, not adding duplicate`);
              return prev;
            }
            
            console.log(`Adding new message with id ${data.message.id} and content: ${data.message.content.substring(0, 50)}...`);
            const newMessages = [...prev, {
              id: data.message.id,
              content: data.message.content,
              role: data.message.role,
              created_at: data.message.created_at,
              metadata: data.message.metadata || {}
            }];
            
            // Emit event with metadata for agent info display
            if (data.message.metadata && Object.keys(data.message.metadata).length > 0) {
              console.log('Emitting agent-info event for assistant message:', data.message.metadata);
              window.dispatchEvent(new CustomEvent('agent-info', {
                detail: {
                  metadata: data.message.metadata,
                  messageId: data.message.id
                }
              }));
            }
            
            // Debug: Log new messages array
            console.log('Updated messages:', newMessages.map(m => ({ id: m.id, role: m.role })));
            
            return newMessages;
          });
          
          // Force a refresh of the current chat if needed
          if (currentChat && currentChat.id) {
            console.log('Scheduling a chat refresh to ensure all messages are loaded');
            setTimeout(() => {
              loadChat(currentChat.id).catch(err => console.error('Error refreshing chat after message:', err));
            }, 500);
          }
          
          setTyping(false);
        } else if (data.event === 'chat_created') {
          // New chat created via WebSocket
          console.log('Chat created via WebSocket:', data.chat.id);
          
          // Update current chat but don't reload the chat list to avoid duplicates
          // (The chat should have been already added when the creation was initiated)
          setCurrentChat(data.chat);
          setMessages([]);
          
          // Check if this chat is already in our list or was just created via REST API
          const isRecentlyCreated = window.lastCreatedChatId === data.chat.id;
          
          if (isRecentlyCreated) {
            console.log(`Chat ${data.chat.id} was just created via REST API, not adding duplicate`);
            // Reset the tracking variable
            window.lastCreatedChatId = null;
          } else {
            // Only update chats if it's not a duplicate of a recently created chat
            setChats(prevChats => {
              const chatExists = prevChats.some(chat => chat.id === data.chat.id);
              if (chatExists) {
                console.log(`Chat ${data.chat.id} already exists in chat list, not adding duplicate`);
                return prevChats;
              } else {
                console.log(`Adding new chat ${data.chat.id} to chat list`);
                return [data.chat, ...prevChats.filter(chat => chat.id !== data.chat.id)];
              }
            });
          }
        } else if (data.event === 'ping') {
          // Received ping from server, respond with pong to keep connection alive
          if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
            try {
              websocket.current.send(JSON.stringify({ event: 'pong' }));
            } catch (err) {
              console.error('Error sending pong:', err);
            }
          }
        } else if (data.event === 'state_confirmed') {
          // Connection state confirmed
          // Connection is healthy, perform any needed state sync
          if (currentChat && currentChat.id) {
            // Refreshing current chat
            loadChat(currentChat.id).catch(err => console.error('Error refreshing chat:', err));
          }
        } else if (data.event === 'error') {
          console.error('WebSocket error from server:', data.message);
          setError(data.message);
        } else if (data.event === 'delivery_confirmation') {
          console.log('Received delivery confirmation for message:', data.message_id);
          // Verify that we have the message in our local state
          setMessages(prev => {
            const messageExists = prev.some(msg => msg.id === data.message_id);
            if (!messageExists && currentChat) {
              console.log(`Message ${data.message_id} not found locally, refreshing chat`);
              loadChat(currentChat.id).catch(err => console.error('Error refreshing chat after delivery confirmation:', err));
            }
            return prev;
          });
        } else if (data.error) {
          setError(data.error);
        } else {
          console.log('Unknown WebSocket message type:', data);
        }
      } catch (err) {
        console.error('Error handling WebSocket message:', err, event.data);
      }
    };
    
    // Connection closed
    websocket.current.onclose = () => {
      console.log('WebSocket connection closed');
    };
    
    // Connection error
    websocket.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('WebSocket connection error');
    };
  }, [token, currentChat, loadChat]);

  // Send message via WebSocket with fallback to REST API
  const sendMessageWs = (message, chatId = null) => {
    // Set loading state
    setLoading(true);
    setTyping(true);
    
    // Generate a message timestamp for tracking
    const messageTimestamp = new Date().toISOString();
    const messageId = `temp_${Date.now()}`;
    
    // Log message details for debugging
    console.log(`Sending message ${messageId} at ${messageTimestamp}`, { message, chatId });
    
    // Try WebSocket first
    if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
      try {
        console.log('WebSocket is open, sending message via WebSocket');
        // Prepare client data to send with the message
        const clientData = selectedClient ? {
          client_id: selectedClient.id,
          client_name: selectedClient.company_name,
          client_contact: selectedClient.contact_name,
          client_email: selectedClient.email
        } : null;
        
        const data = {
          message,
          chat_id: chatId || currentChat?.id,
          client_message_id: messageId,
          timestamp: messageTimestamp,
          client_data: clientData
        };
        
        // Send via WebSocket
        websocket.current.send(JSON.stringify(data));
        console.log('Message sent via WebSocket, waiting for response');
        
        // Add artificial timeout for WebSocket fallback
        const fallbackTimer = setTimeout(() => {
          // Check if we're still waiting for a response
          if (loading) {
            console.log(`WebSocket timeout after 15 seconds for message ${messageId}, falling back to REST API`);
            
            // Try to force refresh connection
            if (websocket.current) {
              try {
                websocket.current.send(JSON.stringify({ 
                  event: 'check_state',
                  timestamp: new Date().toISOString(),
                  message_id: messageId
                }));
                console.log('Sent connection check before fallback');
              } catch (err) {
                console.error('Error sending connection check:', err);
              }
            }
            
            // Fallback to REST API
            sendMessage(message, chatId).catch(err => {
              console.error('Error in fallback REST API call:', err);
              setLoading(false);
              setTyping(false);
              setError('Failed to send message. Please try again.');
            });
          } else {
            console.log(`WebSocket response received before timeout for message ${messageId}`);
          }
        }, 15000); // Increased timeout to 15 seconds
        
        // Store the timer for potential cleanup
        websocket.current.fallbackTimer = fallbackTimer;
        
      } catch (err) {
        console.error('Error sending via WebSocket:', err);
        // Fall back to REST API
        sendMessage(message, chatId).catch(err => {
          console.error('Error in fallback REST API call:', err);
          setLoading(false);
          setTyping(false);
          setError('Failed to send message. Please try again.');
        });
      }
    } else {
      console.log(`WebSocket not connected (state: ${websocket.current ? websocket.current.readyState : 'null'}), using REST API`);
      // WebSocket not connected, use REST API
      sendMessage(message, chatId).catch(err => {
        console.error('Error sending via REST API:', err);
        setLoading(false);
        setTyping(false);
        setError('Failed to send message. Please try again.');
      });
    }
  };

  // Update chat title
  const updateChatTitle = async (chatId, title) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.put(`/api/v1/chat/${chatId}`, { title });
      
      // Update chat in list
      setChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, title: response.data.title } : chat
      ));
      
      // Update current chat if it's the one being edited
      if (currentChat && currentChat.id === chatId) {
        setCurrentChat(prev => ({ ...prev, title: response.data.title }));
      }
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update chat title');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete chat
  const deleteChat = async (chatId) => {
    try {
      setLoading(true);
      setError(null);
      await axios.delete(`/api/v1/chat/${chatId}`);
      
      // Remove chat from list
      setChats(prev => prev.filter(chat => chat.id !== chatId));
      
      // Clear current chat if it's the one being deleted
      if (currentChat && currentChat.id === chatId) {
        setCurrentChat(null);
        setMessages([]);
      }
      
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete chat');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Initialize WebSocket when token changes
  useEffect(() => {
    console.log('ChatContext token changed:', token ? `${token.substring(0, 10)}...` : 'null');
    if (token) {
      // Small delay to ensure auth header is set
      const timeout = setTimeout(() => {
        console.log('Initializing WebSocket and loading chats...');
        // Re-enabling now that authentication is working
        initWebSocket();
        
        // Try to load chats
        loadChats().catch(err => {
          console.error('Error loading chats:', err);
        });
      }, 500);
      
      // Handle page visibility changes to reconnect when page becomes visible
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && token) {
          // Page became visible, ensure WebSocket is connected
          if (!websocket.current || websocket.current.readyState !== WebSocket.OPEN) {
            setTimeout(() => {
              initWebSocket();
            }, 1000);
          }
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
    
    // Cleanup WebSocket on unmount
    return () => {
      if (websocket.current) {
        console.log('Cleaning up WebSocket connection on unmount');
        
        // Clear intervals if they exist
        if (websocket.current.pingInterval) {
          clearInterval(websocket.current.pingInterval);
        }
        
        if (websocket.current.healthCheckInterval) {
          clearInterval(websocket.current.healthCheckInterval);
        }
        
        websocket.current.close();
      }
    };
  }, [token, initWebSocket, loadChats]);

  // Provide the chat context
  return (
    <ChatContext.Provider
      value={{
        chats,
        currentChat,
        messages,
        loading,
        error,
        typing,
        loadChats,
        loadChat,
        createChat,
        sendMessage,
        sendMessageWs,
        updateChatTitle,
        deleteChat
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
